import { useState, useCallback } from 'react';
import Tesseract from 'tesseract.js';

// Parse Venezuelan mobile payment (Pago Móvil) receipts
const parsePagoMovil = (text) => {
  const result = {
    referencia: null,
    monto: null,
    fecha: null,
    telefono: null,
    banco: null,
  };

  // Normalize text
  const normalized = text.replace(/\n/g, ' ').replace(/\s+/g, ' ');

  // Reference number patterns (various banks)
  const refPatterns = [
    /(?:ref(?:erencia)?|nro\.?\s*(?:de\s*)?ref|transacci[oó]n|oper(?:aci[oó]n)?)[:\s#]*(\d{6,20})/i,
    /(?:comprobante|confirm)[:\s#]*(\d{6,20})/i,
    /(\d{12,20})/,
  ];
  for (const pattern of refPatterns) {
    const match = normalized.match(pattern);
    if (match) {
      result.referencia = match[1];
      break;
    }
  }

  // Amount patterns
  const montoPatterns = [
    /(?:monto|total|bs\.?|bol[ií]vares?)[:\s]*(\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{1,2})?)/i,
    /(\d{1,3}(?:\.\d{3})*,\d{2})\s*(?:bs|bol)/i,
    /(?:bs|bol)[:\s.]*(\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{1,2})?)/i,
  ];
  for (const pattern of montoPatterns) {
    const match = normalized.match(pattern);
    if (match) {
      // Convert Venezuelan format (1.234,56) to number
      let monto = match[1].replace(/\./g, '').replace(',', '.');
      result.monto = parseFloat(monto);
      break;
    }
  }

  // Date patterns
  const fechaPatterns = [
    /(?:fecha)[:\s]*(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})/i,
    /(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})/,
  ];
  for (const pattern of fechaPatterns) {
    const match = normalized.match(pattern);
    if (match) {
      let [, day, month, year] = match;
      if (year.length === 2) year = '20' + year;
      result.fecha = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      break;
    }
  }

  // Phone patterns
  const telPatterns = [
    /(?:tel[eé]fono|desde|origen|cel)[:\s]*(0?\d{3}[-.\s]?\d{7})/i,
    /(04\d{2}[-.\s]?\d{7})/,
    /(0\d{3}[-.\s]?\d{7})/,
  ];
  for (const pattern of telPatterns) {
    const match = normalized.match(pattern);
    if (match) {
      result.telefono = match[1].replace(/[-.\s]/g, '');
      break;
    }
  }

  // Bank patterns
  const bancos = [
    'mercantil', 'provincial', 'venezuela', 'banesco', 'exterior',
    'bicentenario', 'tesoro', 'bnc', 'bancaribe', 'bancamiga',
    'banplus', 'fondo com[uú]n', 'plaza', 'sofitasa', 'activo',
    'caroní', 'caroni', 'bod', 'occidental',
  ];
  const bancoRegex = new RegExp(`(?:banco|bank)[:\\s]*(${bancos.join('|')})`, 'i');
  const bancoMatch = normalized.match(bancoRegex);
  if (bancoMatch) {
    result.banco = bancoMatch[1].charAt(0).toUpperCase() + bancoMatch[1].slice(1).toLowerCase();
  } else {
    // Try matching bank name directly
    for (const banco of bancos) {
      const regex = new RegExp(`\\b${banco}\\b`, 'i');
      if (regex.test(normalized)) {
        result.banco = banco.charAt(0).toUpperCase() + banco.slice(1).toLowerCase();
        break;
      }
    }
  }

  return result;
};

// Custom hook for OCR processing
export const useOCR = () => {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const processReceipt = useCallback(async (imageFile) => {
    setProcessing(true);
    setProgress(0);
    setError(null);

    try {
      const result = await Tesseract.recognize(imageFile, 'spa', {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setProgress(Math.round(m.progress * 100));
          }
        },
      });

      const rawText = result.data.text;
      const parsed = parsePagoMovil(rawText);

      setProcessing(false);
      setProgress(100);

      return {
        success: true,
        rawText,
        data: parsed,
        hasData: Object.values(parsed).some((v) => v !== null),
      };
    } catch (err) {
      console.error('OCR error:', err);
      setError('Error procesando imagen. Intente de nuevo o ingrese los datos manualmente.');
      setProcessing(false);
      return { success: false, rawText: '', data: {}, hasData: false };
    }
  }, []);

  return { processReceipt, processing, progress, error };
};

export default useOCR;
